import { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Archive, Edit, Megaphone, Plus, Trash2 } from 'lucide-react';
import { Button, ManagedDataTable, Select } from '@/design-system';
import type { Announcement, AnnouncementStatus } from '../types';

const STATUS_LABELS: Record<AnnouncementStatus, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  archived: 'Arquivado',
};

const STATUS_CLASS: Record<AnnouncementStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-emerald-100 text-emerald-800',
  archived: 'bg-amber-100 text-amber-800',
};

const AUDIENCE_LABELS = {
  all: 'Todos',
  selected: 'Selecionados',
} as const;

type Props = {
  announcements: Announcement[];
  isLoading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
  statusFilter: AnnouncementStatus | undefined;
  onStatusFilterChange: (v: AnnouncementStatus | undefined) => void;
  onNew: () => void;
  onEdit: (a: Announcement) => void;
  onPublish: (a: Announcement) => void;
  onArchive: (a: Announcement) => void;
  onDelete: (a: Announcement) => void;
};

export function AnnouncementsTable({
  announcements,
  isLoading,
  search,
  onSearchChange,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  statusFilter,
  onStatusFilterChange,
  onNew,
  onEdit,
  onPublish,
  onArchive,
  onDelete,
}: Props) {
  const columns = useMemo<ColumnDef<Announcement>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Título',
        cell: ({ row }) => (
          <div className="text-sm font-medium text-gray-900 max-w-md truncate">
            {row.original.title}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[row.original.status]}`}
          >
            {STATUS_LABELS[row.original.status]}
          </span>
        ),
      },
      {
        accessorKey: 'audience',
        header: 'Audiência',
        cell: ({ row }) => (
          <span className="text-sm text-gray-700">{AUDIENCE_LABELS[row.original.audience]}</span>
        ),
      },
      {
        accessorKey: 'publishedAt',
        header: 'Publicado em',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">
            {row.original.publishedAt
              ? new Date(row.original.publishedAt).toLocaleString('pt-BR')
              : '—'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => {
          const a = row.original;
          return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm"
                type="button"
                className="text-gray-400 hover:text-primary"
                onClick={() => onEdit(a)}
                title="Editar"
                aria-label="Editar"
              >
                <Edit size={16} />
              </Button>
              {a.status === 'draft' && (
                <Button variant="ghost" size="sm"
                  type="button"
                  className="text-gray-400 hover:text-emerald-600"
                  onClick={() => onPublish(a)}
                  title="Publicar"
                  aria-label="Publicar"
                >
                  <Megaphone size={16} />
                </Button>
              )}
              {a.status === 'published' && (
                <Button variant="ghost" size="sm"
                  type="button"
                  className="text-gray-400 hover:text-amber-600"
                  onClick={() => onArchive(a)}
                  title="Arquivar"
                  aria-label="Arquivar"
                >
                  <Archive size={16} />
                </Button>
              )}
              {a.status === 'draft' && (
                <Button variant="ghost" size="sm"
                  type="button"
                  className="text-gray-400 hover:text-red-600"
                  onClick={() => onDelete(a)}
                  title="Excluir"
                  aria-label="Excluir"
                >
                  <Trash2 size={16} />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [onArchive, onDelete, onEdit, onPublish]
  );

  return (
    <div className="space-y-4">
      <ManagedDataTable
        title="Lista de comunicados"
        subtitle="Crie e publique avisos para todas as empresas ou para públicos selecionados."
        toolbarFilters={<div className="w-48">
        <Select
          label="Status"
          value={statusFilter ?? ''}
          onChange={(e) =>
            onStatusFilterChange(
              e.target.value ? (e.target.value as AnnouncementStatus) : undefined
            )
          }
        >
          <option value="">Todos os status</option>
          <option value="draft">Rascunho</option>
          <option value="published">Publicado</option>
          <option value="archived">Arquivado</option>
        </Select>
      </div>}
        columns={columns}
        data={announcements}
        searchPlaceholder="Buscar por título…"
        searchValue={search}
        onSearchChange={onSearchChange}
        loading={isLoading}
        emptyMessage="Nenhum comunicado encontrado."
        pagination={{ page, totalPages, total, limit, onPageChange }}
        toolbarButtons={[
          {
            label: 'Novo comunicado',
            icon: <Plus size={16} />,
            onClick: onNew,
          },
        ]}
      />
    </div>
  );
}
